import type ExcelJS from "exceljs";
import type { DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";

/**
 * Shared, gridline-free .xlsx renderer for the finance reports. Produces the
 * same "styled card on a clean sheet" look as the on-screen tables:
 *   - gridlines hidden, title/header rows + label column frozen
 *   - dark title band, slate column header
 *   - section headers, bold totals, dark grand totals, colour-by-sign values
 *   - live formulas when a cell carries one
 *
 * Callers build a flat ExportRow[] (in display order) and call exportStyledExcel.
 * Data rows are written starting at excel row 3 (row 1 = title, row 2 = header),
 * so a caller building cross-references can compute a row's excel number as
 * `rowArrayIndex + 3`.
 */

export type RowKind =
  | "header" // section band — bold uppercase, no values
  | "data" // normal line — values coloured by sign
  | "total" // subtotal — slate band, bold
  | "grandTotal" // dark band, white bold
  | "highlight" // emerald tint (e.g. P&L contribution / gross profit)
  | "percent" // emerald italic, percentage format
  | "subtotal" // semibold with a top rule (balance sheet)
  | "check"; // amber reconciliation line (balance sheet)

export type ExportCell = {
  value: number;
  /** Column-relative formula WITHOUT a leading "=" (e.g. "B5+B6", "SUM(B5:B7)"). */
  formula?: string;
  /** Overrides the row's default format. Defaults: percent→percent, else currency. */
  format?: "currency" | "percent" | "plain";
};

export type ExportRow = {
  label: string;
  indent?: number;
  kind: RowKind;
  /** One entry per period; null/omitted → blank cell (used for headers). */
  cells?: (ExportCell | null)[];
};

// Palette mirrored from the on-screen tables (Tailwind). exceljs uses ARGB.
const C = {
  slate900: "FF0F172A",
  slate700: "FF334155",
  slate600: "FF475569",
  slate500: "FF64748B",
  slate300: "FFCBD5E1",
  slate200: "FFE2E8F0",
  slate100: "FFF1F5F9",
  slate50: "FFF8FAFC",
  white: "FFFFFFFF",
  emerald800: "FF065F46",
  emerald700: "FF047857",
  emerald600: "FF059669",
  emerald50: "FFECFDF5",
  amber900: "FF78350F",
  amber50: "FFFFFBEB",
  rose500: "FFF43F5E",
  red600: "FFDC2626",
};

const FONT = "Calibri";

function solid(argb: string): ExcelJS.Fill {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function colLetter(index: number): string {
  // 0-based → A, B, ...
  let n = index;
  let s = "";
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

function currencyFmt(currency: DisplayCurrency, decimals: number): string {
  const sym = currency === "USD" ? '"$"' : '"ETB"';
  const num = decimals > 0 ? `#,##0.${"0".repeat(decimals)}` : "#,##0";
  return `${sym} ${num};(${sym} ${num});"–"`;
}

const PCT_FMT = "0.0%";
const PLAIN_FMT = "#,##0";

export type ExcelSheetSpec = {
  title: string;
  sheetName: string;
  periodLabels: { key: string; label: string }[];
  rows: ExportRow[];
  displayCurrency: DisplayCurrency;
  /** Currency decimal places (default 2; balance sheet passes 0). */
  currencyDecimals?: number;
  /** Label column width (default 46). */
  labelColWidth?: number;
};

// Excel sheet names: max 31 chars and cannot contain : \ / ? * [ ].
function safeSheetName(name: string): string {
  return name.replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Sheet";
}

/** Render one styled report worksheet into an existing workbook. */
function renderWorksheet(wb: ExcelJS.Workbook, sheet: ExcelSheetSpec): void {
  const {
    title,
    sheetName,
    periodLabels,
    rows,
    displayCurrency,
    currencyDecimals = 2,
    labelColWidth = 46,
  } = sheet;

  const curFmt = currencyFmt(displayCurrency, currencyDecimals);
  const dataLastCol = periodLabels.length + 1; // 1 = label, 2..N+1 = periods

  const ws = wb.addWorksheet(safeSheetName(sheetName), {
    // Hide gridlines for a clean surface; freeze title + header + label column.
    views: [{ state: "frozen", xSplit: 1, ySplit: 2, showGridLines: false }],
  });

  const applyBorder = (
    cell: ExcelJS.Cell,
    colIdx: number,
    isGrandTotal: boolean,
    topRule = false
  ) => {
    const border: Partial<ExcelJS.Borders> = {
      bottom: {
        style: isGrandTotal ? "medium" : "thin",
        color: { argb: isGrandTotal ? C.slate700 : C.slate100 },
      },
    };
    if (topRule) border.top = { style: "thin", color: { argb: C.slate200 } };
    if (colIdx === 1) border.left = { style: "medium", color: { argb: C.slate200 } };
    if (colIdx === dataLastCol)
      border.right = { style: "medium", color: { argb: C.slate200 } };
    cell.border = border;
  };

  // ── Row 1: dark title band (title merged; currency right-aligned) ───────
  const titleMergeEnd = dataLastCol - 1;
  if (titleMergeEnd > 1) ws.mergeCells(1, 1, 1, titleMergeEnd);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { name: FONT, size: 14, bold: true, color: { argb: C.white } };
  titleCell.fill = solid(C.slate900);
  titleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
  titleCell.border = { left: { style: "medium", color: { argb: C.slate200 } } };
  for (let c = 2; c < dataLastCol; c++) ws.getCell(1, c).fill = solid(C.slate900);
  const metaCell = ws.getCell(1, dataLastCol);
  metaCell.value = `Currency: ${displayCurrency}`;
  metaCell.font = { name: FONT, size: 10, color: { argb: C.slate300 } };
  metaCell.fill = solid(C.slate900);
  metaCell.alignment = { horizontal: "right", vertical: "middle", indent: 1 };
  metaCell.border = { right: { style: "medium", color: { argb: C.slate200 } } };

  // ── Row 2: column header ────────────────────────────────────────────────
  const headerCell = (col: number, text: string, align: "left" | "right") => {
    const cell = ws.getCell(2, col);
    cell.value = text;
    cell.font = { name: FONT, size: 9, bold: true, color: { argb: C.slate500 } };
    cell.fill = solid(C.slate50);
    cell.alignment = { horizontal: align, vertical: "middle", indent: 1 };
    const border: Partial<ExcelJS.Borders> = {
      top: { style: "thin", color: { argb: C.slate200 } },
      bottom: { style: "medium", color: { argb: C.slate200 } },
    };
    if (col === 1) border.left = { style: "medium", color: { argb: C.slate200 } };
    if (col === dataLastCol)
      border.right = { style: "medium", color: { argb: C.slate200 } };
    cell.border = border;
  };
  headerCell(1, "LINE ITEM", "left");
  periodLabels.forEach((p, i) => headerCell(i + 2, p.label.toUpperCase(), "right"));

  // ── Data rows ───────────────────────────────────────────────────────────
  let excelRow = 3;
  for (const r of rows) {
    const isGrand = r.kind === "grandTotal";
    const topRule = r.kind === "subtotal";

    // Row-level fill + label styling by kind.
    let rowFill: string;
    let labelColor: string;
    let labelBold: boolean;
    let labelItalic = false;
    switch (r.kind) {
      case "grandTotal":
        rowFill = C.slate900;
        labelColor = C.white;
        labelBold = true;
        break;
      case "header":
      case "total":
        rowFill = C.slate50;
        labelColor = C.slate900;
        labelBold = true;
        break;
      case "highlight":
        rowFill = C.emerald50;
        labelColor = C.emerald800;
        labelBold = true;
        break;
      case "percent":
        rowFill = C.emerald50;
        labelColor = C.emerald700;
        labelBold = true;
        labelItalic = true;
        break;
      case "subtotal":
        rowFill = C.white;
        labelColor = C.slate900;
        labelBold = true;
        break;
      case "check":
        rowFill = C.amber50;
        labelColor = C.amber900;
        labelBold = true;
        break;
      default: // data
        rowFill = C.white;
        labelColor = C.slate600;
        labelBold = false;
    }

    const labelCell = ws.getCell(excelRow, 1);
    labelCell.value = r.label;
    labelCell.font = {
      name: FONT,
      size: r.kind === "header" ? 9 : 10,
      bold: labelBold,
      italic: labelItalic,
      color: { argb: labelColor },
    };
    labelCell.fill = solid(rowFill);
    labelCell.alignment = {
      horizontal: "left",
      vertical: "middle",
      indent: 1 + (r.indent ?? 0),
    };
    applyBorder(labelCell, 1, isGrand, topRule);

    for (let c = 0; c < periodLabels.length; c++) {
      const cell = ws.getCell(excelRow, c + 2);
      cell.fill = solid(rowFill);
      applyBorder(cell, c + 2, isGrand, topRule);

      const spec = r.cells?.[c] ?? null;
      if (r.kind === "header" || !spec) continue; // blank cell

      const val = spec.value;
      cell.value = spec.formula ? { formula: spec.formula } : val;

      const fmtKind = spec.format ?? (r.kind === "percent" ? "percent" : "currency");
      cell.numFmt =
        fmtKind === "percent" ? PCT_FMT : fmtKind === "plain" ? PLAIN_FMT : curFmt;

      // Value colour by kind, then by sign for plain data rows.
      let valColor: string;
      let valBold = false;
      switch (r.kind) {
        case "grandTotal":
          valColor = C.white;
          valBold = true;
          break;
        case "total":
        case "subtotal":
          valColor = C.slate900;
          valBold = true;
          break;
        case "highlight":
          valColor = C.emerald800;
          valBold = true;
          break;
        case "percent":
          valColor = C.emerald700;
          valBold = true;
          break;
        case "check":
          valColor = Math.abs(val) > 1 ? C.red600 : C.amber900;
          valBold = Math.abs(val) > 1;
          break;
        default: // data — colour by sign like the UI
          valColor = val > 0 ? C.emerald600 : val < 0 ? C.rose500 : C.slate300;
      }
      cell.font = { name: FONT, size: 10, bold: valBold, color: { argb: valColor } };
      cell.alignment = { horizontal: "right", vertical: "middle", indent: 1 };
    }

    excelRow += 1;
  }

  // Column widths + row heights.
  ws.getColumn(1).width = labelColWidth;
  for (let c = 2; c <= dataLastCol; c++) ws.getColumn(c).width = 16;
  ws.getRow(1).height = 30;
  ws.getRow(2).height = 22;

}

// Download a workbook (mirrors the repo's Blob/anchor pattern).
async function downloadWorkbook(wb: ExcelJS.Workbook, fileName: string): Promise<void> {
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Export a single styled report worksheet as an .xlsx download. */
export async function exportStyledExcel(
  opts: ExcelSheetSpec & { fileName: string }
): Promise<void> {
  // Lazy-load exceljs so it is code-split out of the main bundle (only needed
  // when a user actually exports).
  const ExcelJSLib = (await import("exceljs")).default;
  const wb = new ExcelJSLib.Workbook();
  renderWorksheet(wb, opts);
  await downloadWorkbook(wb, opts.fileName);
}

/**
 * One workbook, many styled report sheets — e.g. both currencies × the three
 * finance reports for the combined investor download.
 */
export async function exportStyledExcelMulti(
  sheets: ExcelSheetSpec[],
  fileName: string
): Promise<void> {
  const ExcelJSLib = (await import("exceljs")).default;
  const wb = new ExcelJSLib.Workbook();
  for (const sheet of sheets) renderWorksheet(wb, sheet);
  await downloadWorkbook(wb, fileName);
}

/** Helper for callers building column-relative formulas: 0-based period index → column letter. */
export function periodColLetter(periodIndex: number): string {
  return colLetter(periodIndex + 1); // +1 skips the label column (A)
}
